import config from '../config';
import { normalizeTeamSlot } from '../utils/tradeTeamUtils';

function formatApiDetail(detail) {
  if (!detail) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => (typeof item === 'string' ? item : item.msg || JSON.stringify(item))).join('; ');
  }
  return String(detail);
}

class TradeApi {
  constructor() {
    this.baseUrl = config.API_URL;
  }

  /** Prefer TeamRoster JSON (source of truth for trades); fall back to /teams Player rows. */
  async getTeamRoster(teamName, leagueId) {
    const canonical = normalizeTeamSlot(teamName);
    try {
      const rosterRes = await fetch(
        `${this.baseUrl}/leagues/${leagueId}/rosters?team_name=${encodeURIComponent(canonical)}`,
        { credentials: 'include' }
      );
      if (rosterRes.ok) {
        const data = await rosterRes.json();
        const row = (data.rosters || []).find(
          (r) => normalizeTeamSlot(r.team_name) === canonical
        );
        if (row?.roster_data?.length) {
          return row.roster_data.map((p) => ({
            ...p,
            player_id: p.player_id != null ? String(p.player_id) : undefined,
            id: p.id != null ? String(p.id) : undefined,
          }));
        }
        if (row && Array.isArray(row.roster_data)) {
          return [];
        }
      }
    } catch (error) {
      console.error('Error fetching team roster from /rosters:', error);
    }

    try {
      const response = await fetch(`${this.baseUrl}/leagues/${leagueId}/teams`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        const teams = data.teams || [];
        const team = teams.find((t, index) => {
          if (normalizeTeamSlot(t.name) === canonical) return true;
          if (t.name === teamName) return true;
          const slotFromIndex = `Team${index + 1}`;
          if (normalizeTeamSlot(slotFromIndex) === canonical) return true;
          return false;
        });
        return (team?.roster || []).map((p) => ({
          ...p,
          player_id: p.player_id != null ? String(p.player_id) : String(p.id),
          id: p.id != null ? String(p.id) : undefined,
        }));
      }
      return [];
    } catch (error) {
      console.error('Error fetching team roster:', error);
      return [];
    }
  }

  // Get all trades for a league
  async getTrades(leagueId) {
    try {
      const response = await fetch(`${this.baseUrl}/leagues/${leagueId}/trades`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        return data.trades || [];
      }
      return []; // fallback
    } catch (error) {
      console.error('Error fetching trades:', error);
      return []; // fallback
    }
  }

  // Create a new trade
  async createTrade(leagueId, tradeData) {
    try {
      const response = await fetch(`${this.baseUrl}/leagues/${leagueId}/trades`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tradeData),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        return { success: true, id: data.id };
      }
      const err = await response.json().catch(() => ({}));
      return { success: false, detail: formatApiDetail(err.detail) || response.statusText };
    } catch (error) {
      console.error('Error creating trade:', error);
      return { success: false, detail: error.message };
    }
  }

  async submitCounter(leagueId, tradeId, tradeData, currentUser = null) {
    try {
      const response = await fetch(
        `${this.baseUrl}/leagues/${leagueId}/trades/${tradeId}/counter`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...tradeData, user_id: currentUser?.id }),
          credentials: 'include',
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return { success: false, detail: formatApiDetail(err.detail) || response.statusText };
      }
      const data = await response.json();
      return { success: true, id: data.id };
    } catch (error) {
      console.error('Error submitting counter-offer:', error);
      return { success: false, detail: error.message };
    }
  }

  // Update trade status (accept, reject, approve, etc.)
  async updateTrade(leagueId, tradeId, action, data = {}, currentUser = null) {
    try {
      const payload = {
        action,
        ...data,
        user_id: currentUser?.id,
      };
      if (data.status === 'accept') {
        payload.status = 'accepted';
      } else if (data.status === 'reject') {
        payload.status = 'rejected';
      } else if (data.status) {
        payload.status = data.status;
      }
      const response = await fetch(`${this.baseUrl}/leagues/${leagueId}/trades/${tradeId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        credentials: 'include'
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return { success: false, detail: formatApiDetail(err.detail) || response.statusText };
      }
      return { success: true };
    } catch (error) {
      console.error('Error updating trade:', error);
      return { success: false, detail: error.message };
    }
  }

  // Delete a trade
  async deleteTrade(leagueId, tradeId) {
    try {
      const response = await fetch(`${this.baseUrl}/leagues/${leagueId}/trades/${tradeId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      return response.ok;
    } catch (error) {
      console.error('Error deleting trade:', error);
      return false;
    }
  }
}

const tradeApi = new TradeApi();
export default tradeApi;
